/**
 * Dashboard - Premium Design
 */

import { useAppStore } from '../stores';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export function Dashboard() {
    const navigate = useNavigate();
    const { currentApp, apps, collections, fetchApps, fetchCollections } = useAppStore();

    useEffect(() => {
        fetchApps();
    }, [fetchApps]);

    useEffect(() => {
        if (currentApp?.id) {
            fetchCollections(currentApp.id);
        }
    }, [currentApp?.id, fetchCollections]);

    // No project selected
    if (!currentApp && apps.length === 0) {
        return (
            <div className="animate-fade-in flex items-center justify-center min-h-[70vh]">
                <div className="card p-12 text-center max-w-lg shadow-2xl border-2" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="w-20 h-20 rounded-3xl bg-indigo-600 flex items-center justify-center text-4xl mx-auto mb-8 shadow-xl shadow-indigo-500/20">
                        ⚖️
                    </div>
                    <h1 className="text-3xl font-black mb-4 tracking-tight" style={{ color: 'var(--text-primary)', fontFamily: 'Outfit, sans-serif' }}>
                        Legal Document Hub
                    </h1>
                    <p className="text-lg mb-10 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                        Manage all your Privacy Policies, Terms of Service, and Legal Disclaimers in one secure place.
                    </p>
                    <button
                        onClick={() => navigate('/projects')}
                        className="btn btn-primary w-full py-4 text-base font-bold uppercase tracking-widest shadow-xl bg-indigo-600 hover:bg-indigo-700"
                    >
                        Create Legal Project
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in max-w-6xl mx-auto pb-12">
            {/* Project Overview Header */}
            <div className="relative mb-12 p-8 rounded-[32px] overflow-hidden group">
                {/* Background Accent */}
                <div
                    className="absolute inset-0 opacity-10 group-hover:opacity-15 transition-opacity"
                    style={{ background: `linear-gradient(135deg, ${currentApp?.color || '#3B82F6'}, transparent)` }}
                ></div>

                <div className="relative flex flex-col md:flex-row md:items-center gap-8">
                    <div
                        className="w-24 h-24 rounded-[28px] flex items-center justify-center text-5xl shadow-2xl transition-transform group-hover:scale-105"
                        style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}
                    >
                        {currentApp?.icon || '📂'}
                    </div>
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-blue-500/10 text-blue-500">Active Workspace</span>
                            <span className="text-sm font-bold opacity-30">/</span>
                            <span className="text-sm font-bold" style={{ color: 'var(--text-muted)' }}>{currentApp?.slug}</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-2" style={{ color: 'var(--text-primary)', fontFamily: 'Outfit, sans-serif' }}>
                            {currentApp?.name || 'My New Project'}
                        </h1>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] uppercase font-bold opacity-40">App ID:</span>
                                <span className="text-xs font-mono font-bold" style={{ color: 'var(--accent)' }}>{(currentApp as any)?.appId || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] uppercase font-bold opacity-40">Store URL:</span>
                                <span className="text-xs font-medium truncate max-w-[200px]" style={{ color: 'var(--text-secondary)' }}>{(currentApp as any)?.appUrl || 'N/A'}</span>
                            </div>
                        </div>
                        <p className="max-w-xl text-lg font-medium opacity-80" style={{ color: 'var(--text-secondary)' }}>
                            {currentApp?.description || 'Manage legal pages and compliance documents for this application.'}
                        </p>
                    </div>

                    <div className="md:ml-auto flex flex-col gap-2">
                        <button onClick={() => navigate('/schema')} className="btn btn-primary px-8">
                            Add Content Type
                        </button>
                        <button onClick={() => navigate('/settings')} className="btn btn-secondary px-8">
                            Project Settings
                        </button>
                    </div>
                </div>
            </div>

            {/* Core Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                {[
                    { label: 'Cloudflare D1', value: 'Live', color: 'text-orange-500', icon: '🧡' },
                    { label: 'Storage (R2)', value: 'Active', color: 'text-blue-500', icon: '☁️' },
                    { label: 'Security', value: 'Hardened', color: 'text-emerald-500', icon: '🛡️' },
                    { label: 'DB Latency', value: '< 10ms', color: 'text-indigo-500', icon: '⚡' }
                ].map((stat, i) => (
                    <div key={i} className="card p-6 border-b-4 border-b-blue-500/20 hover:border-b-blue-500 transition-all">
                        <div className="flex items-center justify-between mb-4">
                            <span className="text-2xl">{stat.icon}</span>
                            <span className="text-[10px] font-black uppercase tracking-widest opacity-50">7 day report</span>
                        </div>
                        <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: 'var(--text-muted)' }}>{stat.label}</p>
                        <h3 className={`text-4xl font-black ${stat.color}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{stat.value}</h3>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Content Types List */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="flex items-center justify-between px-2">
                        <h2 className="text-xl font-black tracking-tight" style={{ color: 'var(--text-primary)', fontFamily: 'Outfit, sans-serif' }}>
                            Content Structures
                        </h2>
                    </div>

                    {collections.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {collections.map((col) => (
                                <button
                                    key={col.id}
                                    onClick={() => navigate(`/collections/${col.id}`)}
                                    className="card p-6 text-left group hover:border-blue-500/50"
                                >
                                    <div className="flex items-center gap-5">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl transition-transform group-hover:scale-110"
                                            style={{ backgroundColor: 'var(--bg-tertiary)' }}
                                        >
                                            {col.icon || '📄'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-lg mb-1 group-hover:text-blue-500 transition-colors" style={{ color: 'var(--text-primary)' }}>
                                                {col.name}
                                            </h3>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-bold uppercase tracking-widest opacity-60">{col.fields?.length || 0} fields</span>
                                                <span className="w-1 h-1 rounded-full bg-current opacity-20"></span>
                                                <span className="text-[10px] font-bold uppercase tracking-widest opacity-60">Ready</span>
                                            </div>
                                        </div>
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                            →
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="card p-12 text-center border-dashed border-2" style={{ backgroundColor: 'transparent', borderColor: 'var(--border-color)' }}>
                            <p className="text-lg font-bold mb-6" style={{ color: 'var(--text-muted)' }}>
                                No content structures defined yet.
                            </p>
                            <button onClick={() => navigate('/schema')} className="btn btn-secondary px-6">
                                Create Your First Collection
                            </button>
                        </div>
                    )}
                </div>

                {/* Vertical Sidebar / Activity */}
                <div className="space-y-6">
                    <h2 className="text-xl font-black tracking-tight px-2" style={{ color: 'var(--text-primary)', fontFamily: 'Outfit, sans-serif' }}>
                        Quick Tips
                    </h2>
                    <div className="space-y-4">
                        {[
                            { title: 'Public Access', text: 'Enable Public Read API in settings to fetch content without tokens.' },
                            { title: 'Custom Slug', text: 'You can define custom slugs for your collections during setup.' },
                            { title: 'Editor Power', text: 'Use Ctrl+B or Ctrl+I in the editor for quick formatting.' }
                        ].map((tip, i) => (
                            <div key={i} className="card p-5 !bg-transparent border-dashed">
                                <h4 className="font-bold text-sm mb-2" style={{ color: 'var(--text-primary)' }}>{tip.title}</h4>
                                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{tip.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
